// Typed API client for the affiliate operator-payout BFF surface.
//
// Bound to BE routes under /v2/admin/bff/affiliate/* shipped in
// gao-id-worker PR #146. Every call goes through `apiCall`, which
// attaches the bearer session token and surfaces 401 as a session-
// expired flag. No HMAC, no service secret, no private key ever
// touches this browser bundle.
//
// Hard rules:
//   * Withdrawal approve + pay are money_movement actions on the BE.
//     They REQUIRE an approved multisign proposalId. The FE does not
//     create proposals from this surface; the operator must collect
//     quorum at /approvals/:id first and paste the proposalId here.
//   * Withdrawal reject + redemption apply / reject are operator-
//     records / low_config actions on the BE — no on-chain side-
//     effect, no proposalId needed.
//   * No buyer wallet / email / IP / userAgent / customer identifier
//     is rendered or requested. The BE redacts before sending.
//
// Response shapes mirror the BE source defensively — fields known to
// exist are typed; anything else stays `unknown` and surfaces as a
// TypeScript error at the call site rather than a silent runtime
// mismatch.

import { apiCall } from "./client";

// ─── Shared status enums ───────────────────────────────────────────

export type AffiliateWithdrawalStatus =
  | "queued"
  | "approved"
  | "paid"
  | "rejected";

export type AffiliateRedemptionStatus = "queued" | "applied" | "rejected";

export type AffiliateWithdrawalStatusFilter =
  | AffiliateWithdrawalStatus
  | "all";

export type AffiliateRedemptionStatusFilter =
  | AffiliateRedemptionStatus
  | "all";

// ─── Withdrawal rows ───────────────────────────────────────────────

/** Row shape returned by `GET /affiliate/withdrawals` (list `items`)
 *  and `GET /affiliate/withdrawals/:id` (bare object). Mirrors the BE
 *  `WithdrawalOperatorView` (gao-id-worker affiliate-operator-payouts.ts):
 *  the BE sends exactly the non-optional fields below.
 *
 *  The trailing `?` fields are NOT emitted by the list/detail operator
 *  view — they are display-only conveniences the UI tolerates as absent
 *  (e.g. `blockNumber` only ever appears on the pay action response).
 *  Keeping them optional keeps the type honest about what the BE sends. */
export interface AffiliateWithdrawalRow {
  id: string;
  identityId: string;
  amountMinor: number;
  currency: string;
  destination: string | null;
  status: AffiliateWithdrawalStatus;
  txHash: string | null;
  rejectionReason: string | null;
  requestedAt: string;
  approvedAt: string | null;
  paidAt: string | null;
  cancelledAt: string | null;
  // Not part of the BE operator view — display-only / other surfaces.
  amountHumanReadable?: string | null;
  rejectedAt?: string | null;
  proposalId?: string | null;
  blockNumber?: number | null;
  metadata?: Record<string, unknown> | null;
}

// BE source of truth (gao-id-worker GET /v2/admin/bff/affiliate/withdrawals)
// returns `{ items, nextCursor }` — no `ok` envelope, and the list key is
// `items`, NOT `withdrawals`. Mirror that shape exactly so the page reads the
// rows the BE actually sends.
export interface AffiliateWithdrawalsListResponse {
  items: AffiliateWithdrawalRow[];
  nextCursor?: string | null;
}

// BE `GET /affiliate/withdrawals/:id` returns the row OBJECT directly
// (no `{ ok, withdrawal }` envelope) — `c.json(toWithdrawalOperatorView(row))`.
export type AffiliateWithdrawalDetailResponse = AffiliateWithdrawalRow;

// ─── Redemption rows ───────────────────────────────────────────────

/** Mirrors the BE `RedemptionOperatorView`. The BE sends exactly the
 *  non-optional fields; trailing `?` fields are display-only and not
 *  emitted by the list/detail operator view. */
export interface AffiliateRedemptionRow {
  id: string;
  identityId: string;
  amountMinor: number;
  currency: string;
  target: string;
  status: AffiliateRedemptionStatus;
  rejectionReason: string | null;
  requestedAt: string;
  appliedAt: string | null;
  cancelledAt: string | null;
  // Not part of the BE operator view — display-only.
  amountHumanReadable?: string | null;
  rejectedAt?: string | null;
  metadata?: Record<string, unknown> | null;
}

// BE source of truth (gao-id-worker GET /v2/admin/bff/affiliate/redemptions)
// returns `{ items, nextCursor }` — same envelope as the withdrawals list.
export interface AffiliateRedemptionsListResponse {
  items: AffiliateRedemptionRow[];
  nextCursor?: string | null;
}

// BE `GET /affiliate/redemptions/:id` likewise returns the row directly.
export type AffiliateRedemptionDetailResponse = AffiliateRedemptionRow;

// ─── Action response shapes ────────────────────────────────────────
//
// The BE withdrawal action routes do NOT return the full row — each
// returns a small status-transition object:
//   approve → { id, status: "approved", approvedAt }
//   pay     → { id, status: "paid", txHash, blockNumber, paidAt }
//   reject  → { id, status: "rejected", rejectedAt }
// One union-friendly shape covers all three (timestamps optional).
export interface AffiliateWithdrawalActionResponse {
  id: string;
  status: AffiliateWithdrawalStatus;
  approvedAt?: string;
  paidAt?: string;
  rejectedAt?: string;
  txHash?: string;
  blockNumber?: number;
}

// Redemption action routes:
//   apply  → { id, status: "applied", appliedAt }
//   reject → { id, status: "rejected", rejectedAt }
export interface AffiliateRedemptionActionResponse {
  id: string;
  status: AffiliateRedemptionStatus;
  appliedAt?: string;
  rejectedAt?: string;
}

// ─── List + detail calls ───────────────────────────────────────────

export interface ListWithdrawalsInput {
  status?: AffiliateWithdrawalStatusFilter;
  cursor?: string;
  limit?: number;
}

export async function listAffiliateWithdrawals(
  input: ListWithdrawalsInput = {},
): Promise<AffiliateWithdrawalsListResponse> {
  const query: Record<string, string | undefined> = {};
  if (input.status && input.status !== "all") query["status"] = input.status;
  if (input.cursor) query["cursor"] = input.cursor;
  if (typeof input.limit === "number") query["limit"] = String(input.limit);
  return apiCall<AffiliateWithdrawalsListResponse>(
    "/v2/admin/bff/affiliate/withdrawals",
    { query },
  );
}

export async function getAffiliateWithdrawal(
  id: string,
): Promise<AffiliateWithdrawalDetailResponse> {
  return apiCall<AffiliateWithdrawalDetailResponse>(
    `/v2/admin/bff/affiliate/withdrawals/${encodeURIComponent(id)}`,
  );
}

// ─── Auto-linked payout proposal (read-only) ───────────────────────
//
// BE `GET /v2/admin/bff/affiliate/withdrawals/:id/proposal` returns the
// single multisign proposal whose stored payload byte-matches THIS row
// (gao-id-worker PR #190). The BE owns the match — treasury / refund /
// wrong-row / wrong-amount / wrong-destination proposals are never
// returned — so the operator never pastes a proposalId in the normal
// flow. `payoutProposal` is null when no matching proposal exists yet.
export interface AffiliatePayoutProposal {
  id: string;
  /** Effective status; a pending/approved proposal past its expiry is
   *  reported as `"expired"` (BE computes this read-only). */
  status: "proposed" | "approved" | "executed" | "rejected" | "expired" | "cancelled";
  approvalsCount: number;
  thresholdRequired: number;
  isApproved: boolean;
  /** Safe to drive Approve (queued → approved) with this proposal. */
  isUsableForApprove: boolean;
  /** Safe to drive Pay (approved → paid) with this proposal. */
  isUsableForPay: boolean;
  expiresAt: string | null;
  executedAt: string | null;
  executedTxHash: string | null;
  mismatchReason: string | null;
}

export interface AffiliateWithdrawalProposalLinkResponse {
  payoutProposal: AffiliatePayoutProposal | null;
}

export async function getAffiliateWithdrawalProposal(
  id: string,
): Promise<AffiliateWithdrawalProposalLinkResponse> {
  return apiCall<AffiliateWithdrawalProposalLinkResponse>(
    `/v2/admin/bff/affiliate/withdrawals/${encodeURIComponent(id)}/proposal`,
  );
}

export interface ListRedemptionsInput {
  status?: AffiliateRedemptionStatusFilter;
  cursor?: string;
  limit?: number;
}

export async function listAffiliateRedemptions(
  input: ListRedemptionsInput = {},
): Promise<AffiliateRedemptionsListResponse> {
  const query: Record<string, string | undefined> = {};
  if (input.status && input.status !== "all") query["status"] = input.status;
  if (input.cursor) query["cursor"] = input.cursor;
  if (typeof input.limit === "number") query["limit"] = String(input.limit);
  return apiCall<AffiliateRedemptionsListResponse>(
    "/v2/admin/bff/affiliate/redemptions",
    { query },
  );
}

export async function getAffiliateRedemption(
  id: string,
): Promise<AffiliateRedemptionDetailResponse> {
  return apiCall<AffiliateRedemptionDetailResponse>(
    `/v2/admin/bff/affiliate/redemptions/${encodeURIComponent(id)}`,
  );
}

// ─── Create the money_movement proposal (browser-session, no HMAC) ──
//
// Server-side mints the money_movement proposal that approve + pay
// consume. The browser supplies only an optional `ttlSec`; the BE binds
// the authoritative queueRowId / identityId / amount / destination /
// token / chain from the row + payment config. This is NOT the owner-
// scope HMAC propose endpoint, and NOT the bucket-level escrow
// withdraw-affiliate proposal endpoint — it is the per-row BFF route.
// The returned proposal is in `proposed` state; the operator approves it
// via /approvals before approve/pay can use the proposalId.

export interface AffiliateWithdrawalProposalResponse {
  proposalId: string;
  actionType: "money_movement";
  withdrawalId: string;
  status: string;
}

export async function createAffiliateWithdrawalProposal(
  id: string,
  input: { ttlSec?: number } = {},
): Promise<AffiliateWithdrawalProposalResponse> {
  const body: Record<string, unknown> = {};
  if (typeof input.ttlSec === "number") body["ttlSec"] = input.ttlSec;
  return apiCall<AffiliateWithdrawalProposalResponse>(
    `/v2/admin/bff/affiliate/withdrawals/${encodeURIComponent(id)}/proposals`,
    { method: "POST", body },
  );
}

// ─── Withdrawal mutations ──────────────────────────────────────────

export async function approveAffiliateWithdrawal(
  id: string,
  input: { proposalId: string },
): Promise<AffiliateWithdrawalActionResponse> {
  return apiCall<AffiliateWithdrawalActionResponse>(
    `/v2/admin/bff/affiliate/withdrawals/${encodeURIComponent(id)}/approve`,
    { method: "POST", body: { proposalId: input.proposalId } },
  );
}

export async function payAffiliateWithdrawal(
  id: string,
  input: { proposalId: string },
): Promise<AffiliateWithdrawalActionResponse> {
  return apiCall<AffiliateWithdrawalActionResponse>(
    `/v2/admin/bff/affiliate/withdrawals/${encodeURIComponent(id)}/pay`,
    { method: "POST", body: { proposalId: input.proposalId } },
  );
}

export async function rejectAffiliateWithdrawal(
  id: string,
  input: { reason: string },
): Promise<AffiliateWithdrawalActionResponse> {
  return apiCall<AffiliateWithdrawalActionResponse>(
    `/v2/admin/bff/affiliate/withdrawals/${encodeURIComponent(id)}/reject`,
    { method: "POST", body: { reason: input.reason } },
  );
}

// ─── Redemption mutations ──────────────────────────────────────────

export async function applyAffiliateRedemption(
  id: string,
): Promise<AffiliateRedemptionActionResponse> {
  return apiCall<AffiliateRedemptionActionResponse>(
    `/v2/admin/bff/affiliate/redemptions/${encodeURIComponent(id)}/apply`,
    { method: "POST", body: {} },
  );
}

export async function rejectAffiliateRedemption(
  id: string,
  input: { reason: string },
): Promise<AffiliateRedemptionActionResponse> {
  return apiCall<AffiliateRedemptionActionResponse>(
    `/v2/admin/bff/affiliate/redemptions/${encodeURIComponent(id)}/reject`,
    { method: "POST", body: { reason: input.reason } },
  );
}
